import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  History, 
  FolderOpen, 
  Heart, 
  User,
  LogOut,
  Menu,
  X
} from 'lucide-react';

const InvestorLayout = ({ children }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Menü öğeleri
  const menuItems = [
    {
      name: t('investor.menu.dashboard'),
      path: '/tr/investor/dashboard',
      icon: LayoutDashboard,
    },
    {
      name: t('investor.menu.investments'),
      path: '/tr/investor/investments',
      icon: Building2,
    },
    {
      name: t('investor.menu.transactions'),
      path: '/tr/investor/transactions',
      icon: History,
    },
    {
      name: t('investor.menu.documents'),
      path: '/tr/investor/documents',
      icon: FolderOpen,
    },
    {
      name: t('investor.menu.favorites'),
      path: '/tr/investor/favorites',
      icon: Heart,
    },
    {
      name: t('investor.menu.profile'),
      path: '/tr/investor/profile',
      icon: User,
    },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/tr/giris');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 rounded-md hover:bg-gray-100 transition-colors"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <h1 className="text-lg font-semibold text-[#0A192F]">
          {t('investor.dashboard.title')}
        </h1>
        <div className="w-10" /> {/* Spacer for alignment */}
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full bg-[#0A192F] text-white w-64 transform transition-transform duration-300 ease-in-out z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
      >
        {/* Logo/Brand */}
        <div className="p-6 border-b border-white/10">
          <Link to="/tr" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-[#D4AF37] rounded flex items-center justify-center text-white font-bold text-sm">
              OI
            </div>
            <span className="text-xl font-heading">OlyInvestment</span>
          </Link>
          <p className="text-sm text-white/60 mt-2">{t('investor.panel.subtitle')}</p>
        </div>

        {/* Navigation Menu */}
        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-[#D4AF37] text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon size={20} />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Profile & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center space-x-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center text-white font-semibold">
              {profile?.full_name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {profile?.full_name || 'User'}
              </p>
              <p className="text-xs text-white/60 truncate">{profile?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-white/80 hover:bg-red-500/20 hover:text-red-300 transition-all duration-200"
          >
            <LogOut size={20} />
            <span className="font-medium">{t('auth.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default InvestorLayout;
